<template>
    <v-flex xs12>
        <v-sheet
            height="500"
            class="pb-0"
            data-testid="calendar"
            data-gtm="calendar-day-click"
        >
            <v-calendar
                :value="start"
                :type="type"
                :locale="locale + '-ca'"
                :show-month-on-first="false"
                color="primary"
                ref="calendar"
                id="calendar"
                @moved="$emit('updateStart', $event)"
            >
                <template v-slot:day-label="{ date, day, month }">
                    <v-layout v-if="month === monthNumber" fill-height>
                        <v-flex
                            v-if="isDateEvent(date)"
                            xs12
                            class="no-underline-text"
                            @click="eventDayLabelClick(date)"
                            @keyup.enter="eventDayLabelClick(date)"
                        >
                            <v-flex class="hidden-md-and-up">
                                <span class="grey--text text--darken-4 day-label-text">
                                    {{ day }}
                                </span>
                            </v-flex>
                            <v-radio-group :value="selectedDate" class="ma-0 pa-0">
                                <v-radio
                                    :aria-label="getDateDescriptionAria(date)"
                                    :ripple="false"
                                    color="secondary text--darken-4"
                                    :value="date"
                                    style="color: black"
                                    :data-testid="date"
                                    :data-gtm-calendar-day-value="date"
                                    class="ml-1"
                                    tabindex="0"
                                >
                                    <template slot="label">
                                        <span aria-hidden="true" class="grey--text text--darken-4 day-label-text hidden-sm-and-down">
                                            {{ day }}
                                        </span>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </v-flex>
                        <v-flex v-else xs12>
                            <span
                                tabindex="-1"
                                :aria-label="(day + ' - ' + $t('appt.serviceNotAvailable'))"
                                class="grey--text text--darken-4 v-label day-label-text"
                                style="pointer: none"
                            >
                                <span aria-hidden="true">
                                    {{ day }}
                                </span>
                            </span>
                        </v-flex>
                    </v-layout>
                </template>
                <template v-slot:day="{ date, month }">
                    <v-layout v-if="month === monthNumber" fill-height>
                        <v-flex
                            v-if="isDayEvent(date)"
                            :class="getEventColor(date)"
                            xs12
                            :data-testid="'day-event' + date"
                            @click="eventDayClick(date)"
                        >
                            <v-layout v-if="isDatePastEvent(date) || isDateCompleteEvent(date)" row fill-height>
                                <v-flex align-self-center text-center>
                                    <span class="caption">
                                        {{ $t('appt.full') }}
                                    </span>
                                </v-flex>
                            </v-layout>
                            <v-layout v-if="isDateUpcomingEvent(date)" row fill-height>
                                <v-flex align-self-center text-center>
                                    <span class="caption">
                                        {{ $t('appt.upcoming') }}
                                    </span>
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                    <v-layout v-else fill-height>
                        <a class="screenreader-only" href="#calendar-header">
                            {{ $t('appt.selectMonth') }}
                        </a>
                    </v-layout>
                </template>
            </v-calendar>
        </v-sheet>
    </v-flex>
</template>
<script>
import { mapGetters } from 'vuex';
import { some, upperFirst, lowerCase } from 'lodash';
import { eventColor } from '../../helpers/calendar/calendar-helpers';
import { avoidNoAppointmentFocus } from '@/utils';
import { setFocusForAccessibility } from '@/plugins/accessibility';
import { format, isAfter, isBefore } from 'date-fns';
import frLocale from 'date-fns/locale/fr';

export default {
    name: 'calendar',
    props: {
        start: { type: String, required: true },
        type: { type: String, require: true },
        delayTolerance: { type: Object, required: true },
        selectedDate: { type: String, required: false },
        serviceHasVaccineSubmodule: { type: Boolean, required: true },
        serviceHasPregnancyDelaySubmodule: { type: Boolean, required: true },
        calendarTitle: { type: String, required: true },
    },
    mounted() {
        avoidNoAppointmentFocus();
    },
    computed: {
        ...mapGetters({
            locale: 'locale',
            getAvailabilities: 'availabilities/getAvailabilities',
            isDateCompleteEvent: 'availabilities/isDateCompleteEvent',
            isDatePastEvent: 'availabilities/isDatePastEvent',
            isDateUpcomingEvent: 'availabilities/isDateUpcomingEvent',
            selectedService: 'services/getSelectedService',
        }),
        monthNumber: {
            get: function() { return parseInt(this.start.substr(5, 2)); },
        },
    },
    methods: {
        getDateDescriptionAria(date) {
            return this.$t('appt.accessibility.dateDescription.default', { date: this.localizedDay(date), serviceName: this.selectedService['name' + upperFirst(this.locale)] });
        },

        localizedDay(date) {
            let day = this.getDay(date);
            return (this.locale === 'fr') ? day + ' ' + lowerCase(this.calendarTitle) : this.calendarTitle.replace(' ', ` ${day} `);
        },

        getDay(date) { return format(date, 'DD', { locale: this.locale === 'fr' ? frLocale : null }); },
        /**
         * @description Return whether a date is an event
         * @param {Date} date
         * @returns {boolean}
         */
        isDayEvent(date) {
            if (this.isDateCompleteEvent(date) || this.isDatePastEvent(date)) {
                return true;
            }
            if (this.isDateUpcomingEvent(date)) {
                return true;
            }
            return some(this.getAvailabilities, (avail) => {
                const isCurrentDate = avail === date;
                const upcoming = this.isDateUpcomingEvent(date);
                const delayAndTolerance = (!this.serviceHasVaccineSubmodule && !this.serviceHasPregnancyDelaySubmodule) ||
                    (this.isDateInsideTolerance(date) && !this.isDateCompleteEvent(date));
                return isCurrentDate && !upcoming && delayAndTolerance;
            });
        },

        isDateEvent(date) {
            return this.isDayEvent(date) && !this.isDatePastEvent(date) && !this.isDateCompleteEvent(date) && !this.isDateUpcomingEvent(date);
        },

        /**
         * @description Returns whether the date is inside the vaccine delay
         * @param {Date} date
         * @returns {boolean}
         */
        isDateInsideDelay(date) {
            return !isBefore(date, this.delayTolerance.minDelay) &&
                !isAfter(date, this.delayTolerance.maxDelay);
        },

        /**
         * @description Returns whether the date is inside the vaccine delay tolerance
         * @param {Date} date
         * @returns {boolean}
         */
        isDateInsideTolerance(date) {
            return !isBefore(date, this.delayTolerance.minTolerance) &&
                !isAfter(date, this.delayTolerance.maxTolerance);
        },

        /**
         * Returns the color of the event by date with current availabilities
         * @param {Date} date
         * @returns {String}
         */
        getEventColor(date) {
            const isDateCompleteEvent = this.isDateCompleteEvent(date);
            const isDatePastEvent = this.isDatePastEvent(date);
            const isDateUpcomingEvent = this.isDateUpcomingEvent(date);
            const isDateInsideDelay = this.isDateInsideDelay(date);
            const isDateInsideTolerance = this.isDateInsideTolerance(date);

            return eventColor(
                isDateCompleteEvent,
                isDatePastEvent,
                isDateUpcomingEvent,
                this.serviceHasVaccineSubmodule,
                this.serviceHasPregnancyDelaySubmodule,
                isDateInsideDelay,
                isDateInsideTolerance
            );
        },

        eventDayLabelClick(date) {
            this.$emit('dayLabelClick', date);
            this.$nextTick(() => {
                setFocusForAccessibility('apptTimeTitle', 2000, false);
            });
        },

        dayClick(date) {
            this.$emit('dayClick', date);
        },

        eventDayClick(date) {
            if (!this.isDatePastEvent(date) && !this.isDateCompleteEvent(date) && date !== this.selectedDate && !this.isDateUpcomingEvent(date)) {
                this.dayClick(date);
            }
        },
    },
};
</script>

<style lang="scss">
    .complete-avails-background {
        background-color: #daf3f8 !important;
        color: #344054;
    }
    .upcoming-avails-background {
        background-color: #FFF8E5 !important;
        color: #344054;
    }
    .main-avails-background-kids {
        background-color: #aaf3d0 !important;
    }
    .tolerance-avails-background {
        background-color: #daf3f8 !important;
    }
    .tolerance-avails-background {
        background-color: #daf3f8 !important;
    }
    .main-avails-background {
        background-color: #daf3f8 !important;
    }
    .main-avails-background:hover,
    .tolerance-avails-background:hover,
    .main-avails-background-kids {
        cursor: pointer;
    }
    div.v-calendar-weekly__head{
        div.v-past{
            color: #6e6e6e!important;
        }
        div.v-present{
            color: #00768d!important;
            font-weight: bold;
        }
    }
    .v-calendar-weekly__day.v-present .v-calendar-weekly__day-label {
        border: 0;
        color: #000 !important;
    }
    span.day-label-text {
        padding: 5px;
    }
    .v-radio--is-focused {
        background:rgba(0, 255, 0, 1);
        border-radius: 20px;
    }
</style>
